import { useEffect, useState } from "react";
import { Menu, X, User as UserIcon, LogIn, Crown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import useRivalCallouts from "@/hooks/useRivalCallouts";
import AuthModal from "@/components/AuthModal";
import PacificClock from "@/components/PacificClock";
import TokenBalance from "@/components/TokenBalance";
import ChitsTickerPill from "@/components/ChitsTickerPill";
import InnerLightWalletPill from "@/components/InnerLightWalletPill";
import HeroLevelPill from "@/components/HeroLevelPill";
import GuildBuffsBadge from "@/components/GuildBuffsBadge";
import DailyQuestsPill from "@/components/DailyQuestsPill";
import FestivalBoardPill from "@/components/FestivalBoardPill";
import RaidInboxPill from "@/components/RaidInboxPill";
import NotificationsBell from "@/components/NotificationsBell";
import InventoryBag, { InventoryBagButton } from "@/components/InventoryBag";
import GlobalSearch, { GlobalSearchButton } from "@/components/GlobalSearch";

const NAV = [
  { id: "manifesto", label: "Manifesto" },
  { id: "plans", label: "Plans" },
  { id: "tools", label: "Tools" },
  { id: "train", label: "Train", isDropdown: true },
  { id: "shop", label: "Shop" },
  { id: "travel", label: "Travel", isDropdown: true },
  { id: "arcade", label: "Arcade" },
  { id: "boards", label: "🏆 Boards" },
  { id: "champions", label: "Hall of Champions" },
  { id: "hall", label: "Hall" },
];

// 🏋️ Coaching + wellness reachable via the Train dropdown
const TRAIN_ITEMS = [
  { id: "tracker",  label: "Tracker",  icon: "📊", desc: "Workouts, weights, streaks" },
  { id: "food",     label: "Food",     icon: "🍎", desc: "Log meals + Food Vision" },
  { id: "ai-coach", label: "AI Coach", icon: "🤖", desc: "Personalized plans & guidance" },
  { id: "interactive_gym",     label: "Interactive Gym",     icon: "🏋️🌿", desc: "Real-world holistic session — Same-Muscle-Different-Path · breathing pacer · 2–200 min rest timer" },
  { id: "interactive_kitchen", label: "Interactive Kitchen", icon: "🍳🌿", desc: "Real-world holistic meal — soft-food anchors · AI Chef Coach · 2–200 min simmer timer" },
];

// 🗺️ Places reachable via the Travel dropdown
const TRAVEL_PLACES = [
  { id: "nest",       label: "Nest",    icon: "🪺", path: "/nest",       desc: "Your home base" },
  { id: "farm",       label: "Farm",    icon: "🌱", path: "/farm",       desc: "Crops, GrainHouse, chores" },
  { id: "wilds",      label: "Wilds",   icon: "🗺️", path: "/wilds",      desc: "Mines, mobs, the South Quadrant" },
  { id: "castle",     label: "Castle",  icon: "🏰", path: "/castle",     desc: "Claim your keep. Raid other drifters." },
  { id: "blacksmith", label: "Smith",   icon: "⚒️", path: "/blacksmith", desc: "Forge weapons and armor" },
  { id: "armory",     label: "Armory",  icon: "🛡️", path: "/equipment",  desc: "Equip the phoenix anatomy — helmet · chest · wings · talons · aura" },
  { id: "kitchen",    label: "Kitchen", icon: "👨‍🍳", path: "/kitchen",   desc: "Dave's Kitchen — cook the recipes you've learned" },
  { id: "kitchen_hub", label: "Kitchen POV Hub", icon: "🍳📊", path: "/kitchen-hub", desc: "3-POV browser — David (Recipe) · Cassandra & Ledger (Budget) · Roc (Macros)" },
  { id: "codex",      label: "Codex",   icon: "📜", path: "/codex",      desc: "NPC journals — every voice that has written about you" },
  { id: "town_hall",  label: "Town Hall", icon: "🏛️", path: "/town-hall", desc: "Quests + licenses — Miss Kitty & Chief Weasley" },
  // 📍 Feb 2026 owner rule: Library, Gym, and Diner are ONLY reachable
  // through the Castle Courtyard hub — no direct nav links here.
  { id: "tracker_calendar", label: "Tracker Calendar", icon: "📅", path: "/tracker-calendar", desc: "Full month view — log missed food, strength & cardio on any day" },
  { id: "hero_class", label: "Hero Class", icon: "🦸", path: "/hero-class", desc: "Choose your path · Soul Keys · class swap — Warrior · Mage · Archer · Ash Rogue" },
  { id: "hero_codex", label: "Hero Codex", icon: "📖", path: "/hero-codex", desc: "Every hero's full kit — Generator · Secondary · Body · Passive · Super Passive · Ultimate · Super Ultimate · Combo" },
  { id: "aether_and_ash", label: "Aether & Ash", icon: "⚡", path: "/aether-and-ash", desc: "Battle Power ladder — Aether (Level · Skills) + Ash (gear · HP · tower · mount · pet)" },
  { id: "hero_preview", label: "Hero Preview", icon: "🖼️", path: "/armory/preview", desc: "Dragonious hero loadout stage — pedestal and empty slots that light up with your gear" },
  { id: "skill_tree", label: "Skill Tree", icon: "🌳", path: "/skill-tree", desc: "Mind · Body · Soul — spend Chronograph Points on each hero's unique tree" },
  { id: "auction",    label: "Auction House", icon: "🏪", path: "/auction-house", desc: "Bid, sell, snipe — player-to-player trading" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [travelOpen, setTravelOpen] = useState(false);
  const [trainOpen, setTrainOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [bagOpen, setBagOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [hallPulse, setHallPulse] = useState(false);
  const [hallTip, setHallTip] = useState(false);
  const [boardsPulse, setBoardsPulse] = useState(false);
  const [boardsTip, setBoardsTip] = useState(false);
  const { user } = useAuth();
  // 💌🥊 Rival callouts poll — background 5-min ticker; toasts hearts
  // milestones from the friend circle. Non-toxic; celebrates the sender.
  useRivalCallouts(user?.user_id);

  // Hall of Phoenix discoverability: pulse the HALL nav link for the first
  // 7 days after a visitor first sees it, then auto-quiet. Tooltip on first
  // hover prompts "see the community's most-loved moments".
  useEffect(() => {
    try {
      const KEY = "omni:hall-pulse-since";
      const now = Date.now();
      const stored = localStorage.getItem(KEY);
      const since = stored ? Number(stored) : null;
      if (!since) {
        localStorage.setItem(KEY, String(now));
        setHallPulse(true);
      } else {
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        setHallPulse(now - since < sevenDaysMs);
      }
      setHallTip(!localStorage.getItem("omni:hall-tip-seen"));
    } catch {
      // ignore localStorage errors (SSR, private mode)
    }
  }, []);

  const dismissHallTip = () => {
    setHallTip(false);
    try { localStorage.setItem("omni:hall-tip-seen", "1"); } catch {/* noop */}
  };

  // 🏆 Boards discoverability — mirror of the Hall pattern. Pulses the
  // Boards nav for the first 7 days after a visitor first sees it, then
  // auto-quiets. Tooltip on first hover explains what the tab is for.
  useEffect(() => {
    try {
      const KEY = "omni:boards-pulse-since";
      const now = Date.now();
      const stored = localStorage.getItem(KEY);
      const since = stored ? Number(stored) : null;
      if (!since) {
        localStorage.setItem(KEY, String(now));
        setBoardsPulse(true);
      } else {
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        setBoardsPulse(now - since < sevenDaysMs);
      }
      setBoardsTip(!localStorage.getItem("omni:boards-tip-seen"));
    } catch {
      // ignore localStorage errors
    }
  }, []);

  const dismissBoardsTip = () => {
    setBoardsTip(false);
    try { localStorage.setItem("omni:boards-tip-seen", "1"); } catch {/* noop */}
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Listen for global "open auth" requests fired by gated section CTAs
  useEffect(() => {
    const onOpenAuth = () => setAuthOpen(true);
    window.addEventListener("omni:open-auth", onOpenAuth);
    return () => window.removeEventListener("omni:open-auth", onOpenAuth);
  }, []);

  // ⌘K / Ctrl+K toggles the global search modal from anywhere in the app.
  useEffect(() => {
    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === "k") {
        e.preventDefault();
        setSearchOpen((s) => !s);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    // Special route navigations (full pages, not anchors on homepage)
    if (id === "plans") {
      window.location.href = "/plans";
      return;
    }
    if (id === "shop") {
      window.location.href = "/shop";
      return;
    }
    if (id === "arcade") {
      window.location.href = "/arcade";
      return;
    }
    if (id === "cardio") {
      window.location.href = "/cardio";
      return;
    }
    if (id === "nest") {
      window.location.href = "/nest";
      return;
    }
    if (id === "hall") {
      window.location.href = "/hall-of-phoenix";
      return;
    }
    if (id === "forums") {
      window.location.href = "/forums";
      return;
    }
    if (id === "pet") {
      window.location.href = "/pet";
      return;
    }
    if (id === "arena") {
      window.location.href = "/arena";
      return;
    }
    if (id === "farm") {
      window.location.href = "/farm";
      return;
    }
    if (id === "wilds") {
      window.location.href = "/wilds";
      return;
    }
    if (id === "blacksmith") {
      window.location.href = "/blacksmith";
      return;
    }
    if (id === "kitchen") {
      window.location.href = "/kitchen";
      return;
    }
    if (id === "kitchen_hub") {
      window.location.href = "/kitchen-hub";
      return;
    }
    if (id === "interactive_kitchen") {
      window.location.href = "/interactive-kitchen";
      return;
    }
    if (id === "interactive_gym") {
      window.location.href = "/interactive-gym";
      return;
    }
    if (id === "champions") {
      window.location.href = "/hall-of-champions";
      return;
    }
    if (id === "boards") {
      window.location.href = "/leaderboards";
      return;
    }
    if (id === "messages") {
      window.location.href = "/messages";
      return;
    }
    if (window.location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goProfile = () => {
    setOpen(false);
    window.location.href = "/profile";
  };

  const isAuthed = user && user !== false;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-7 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-black/85 border-b border-zinc-800" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <button
            data-testid="logo-home"
            onClick={() => {
              // Logged in → fastest path to user's profile (per user feedback).
              // Signed out → home (or scroll-to-top if already there).
              if (isAuthed) {
                if (window.location.pathname === "/profile") {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                } else {
                  window.location.href = "/profile";
                }
                return;
              }
              if (window.location.pathname === "/") {
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                window.location.href = "/";
              }
            }}
            title={isAuthed ? "Go to my profile" : "Home"}
            className="flex items-center gap-3 group"
          >
            <div className="bg-black p-0.5 rounded-full border-2 border-[#6D28D9] group-hover:border-[#DC2626] transition-colors shadow-[0_0_16px_rgba(109,40,217,0.35)]">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/84dtbvvf_1783524637283.png"
                alt="OMNIMUSCLE phoenix medallion"
                className="block h-10 md:h-12 w-10 md:w-12 rounded-full object-cover"
              />
            </div>
            <div className="hidden sm:flex flex-col items-start leading-tight">
              <span className="font-display text-xl md:text-2xl text-[#4C1D95] group-hover:text-[#6D28D9] transition-colors tracking-tight"
                style={{ WebkitTextStroke: "0.5px #6D28D9" }}>
                OMNI<span className="text-[#DC2626]">MUSCLE</span>
              </span>
              <span className="text-[9px] md:text-[10px] uppercase tracking-[0.3em] text-[#6D28D9] font-bold mt-0.5">
                Holistic Healing
              </span>
            </div>
          </button>

          <nav className="hidden lg:flex items-center gap-4 xl:gap-6 ml-4 xl:ml-8 flex-nowrap">
            {NAV.map((n) => {
              if (n.isDropdown && n.id === "train") {
                return (
                  <div key={n.id}
                       className="relative"
                       onMouseEnter={() => setTrainOpen(true)}
                       onMouseLeave={() => setTrainOpen(false)}>
                    <button
                      data-testid="nav-train"
                      className="font-accent text-sm uppercase tracking-widest text-neutral-300 hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1"
                    >
                      <span>🏋️ {n.label}</span>
                      <span className={`text-[10px] transition-transform ${trainOpen ? "rotate-180" : ""}`}>▾</span>
                    </button>
                    {trainOpen && (
                      <div
                        data-testid="nav-train-dropdown"
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-[#0A0A0A] border-2 border-[#D4AF37] shadow-2xl z-50"
                      >
                        <div className="px-4 py-2 border-b border-[#D4AF37]/30">
                          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                            Choose your training
                          </div>
                        </div>
                        <div className="py-1">
                          {TRAIN_ITEMS.map((t) => (
                            <button
                              key={t.id}
                              data-testid={`nav-train-${t.id}`}
                              onClick={() => { setTrainOpen(false); goTo(t.id); }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#D4AF37] hover:text-black transition-colors group"
                            >
                              <span className="text-2xl">{t.icon}</span>
                              <div className="flex-1 min-w-0">
                                <div className="font-accent text-sm uppercase tracking-wider text-neutral-200 group-hover:text-black">
                                  {t.label}
                                </div>
                                <div className="text-[10px] text-neutral-500 group-hover:text-black/70 truncate">
                                  {t.desc}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              if (n.isDropdown && n.id === "travel") {
                return (
                  <div key={n.id}
                       className="relative"
                       onMouseEnter={() => setTravelOpen(true)}
                       onMouseLeave={() => setTravelOpen(false)}>
                    <button
                      data-testid="nav-travel"
                      className="font-accent text-sm uppercase tracking-widest text-neutral-300 hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1"
                    >
                      <span>🗺️ {n.label}</span>
                      <span className={`text-[10px] transition-transform ${travelOpen ? "rotate-180" : ""}`}>▾</span>
                    </button>
                    {travelOpen && (
                      <div
                        data-testid="nav-travel-dropdown"
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-[#0A0A0A] border-2 border-[#D4AF37] shadow-2xl z-50"
                      >
                        <div className="px-4 py-2 border-b border-[#D4AF37]/30">
                          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                            Choose your destination
                          </div>
                        </div>
                        <div className="py-1">
                          {TRAVEL_PLACES.map((p) => (
                            <button
                              key={p.id}
                              data-testid={`nav-travel-${p.id}`}
                              onClick={() => { setTravelOpen(false); window.location.href = p.path; }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#D4AF37] hover:text-black transition-colors group"
                            >
                              <span className="text-2xl">{p.icon}</span>
                              <div className="flex-1 min-w-0">
                                <div className="font-accent text-sm uppercase tracking-wider text-neutral-200 group-hover:text-black">
                                  {p.label}
                                </div>
                                <div className="text-[10px] text-neutral-500 group-hover:text-black/70 truncate">
                                  {p.desc}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              if (n.id === "hall") {
                return (
                  <div key={n.id} className="relative" onMouseEnter={dismissHallTip}>
                    <button
                      data-testid={`nav-${n.id}`}
                      onClick={() => { dismissHallTip(); goTo(n.id); }}
                      className={
                        "font-accent text-sm uppercase tracking-widest hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1.5 " +
                        (hallPulse ? "hall-pulse" : "text-neutral-300")
                      }
                    >
                      <span>{n.label}</span>
                      {hallPulse && (
                        <span
                          data-testid="nav-hall-new-badge"
                          className="hall-new-badge text-[8px] font-bold tracking-[0.15em] bg-[#D4AF37] text-black px-1 py-[1px] leading-none"
                        >
                          NEW
                        </span>
                      )}
                    </button>
                    {hallTip && hallPulse && (
                      <div
                        data-testid="nav-hall-tooltip"
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-[#0A0A0A] border border-[#D4AF37] px-3 py-2.5 shadow-xl z-10"
                      >
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-bold mb-1">🏛️ New</div>
                        <div className="text-xs text-neutral-200 leading-snug">
                          See the community's most-loved moments — 50+ reactions to land in the Hall.
                        </div>
                        <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-[#0A0A0A] border-l border-t border-[#D4AF37] rotate-45" />
                      </div>
                    )}
                  </div>
                );
              }
              if (n.id === "boards") {
                return (
                  <div key={n.id} className="relative" onMouseEnter={dismissBoardsTip}>
                    <button
                      data-testid={`nav-${n.id}`}
                      onClick={() => { dismissBoardsTip(); goTo(n.id); }}
                      className={
                        "font-accent text-sm uppercase tracking-widest hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1.5 " +
                        (boardsPulse ? "hall-pulse" : "text-neutral-300")
                      }
                    >
                      <span>{n.label}</span>
                      {boardsPulse && (
                        <span
                          data-testid="nav-boards-new-badge"
                          className="hall-new-badge text-[8px] font-bold tracking-[0.15em] bg-[#D4AF37] text-black px-1 py-[1px] leading-none"
                        >
                          NEW
                        </span>
                      )}
                    </button>
                    {boardsTip && boardsPulse && (
                      <div
                        data-testid="nav-boards-tooltip"
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-[#0A0A0A] border border-[#D4AF37] px-3 py-2.5 shadow-xl z-10"
                      >
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-bold mb-1">🏆 New</div>
                        <div className="text-xs text-neutral-200 leading-snug">
                          Global rankings — Hearts, Personal EXP, Pet Level, Evolution + Friends. See where you stand.
                        </div>
                        <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-[#0A0A0A] border-l border-t border-[#D4AF37] rotate-45" />
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={n.id}
                  data-testid={`nav-${n.id}`}
                  onClick={() => goTo(n.id)}
                  className="font-accent text-sm uppercase tracking-widest text-neutral-300 hover:text-[#D4AF37] transition-colors"
                >
                  {n.label}
                </button>
              );
            })}
            <button
              data-testid="nav-cta"
              onClick={() => goTo("ai-coach")}
              className="btn-omni btn-red"
            >
              Get My Plan
            </button>
            <PacificClock
              size="xs"
              showDate={false}
              testId="header-clock"
              className="ml-1 hidden xl:inline-flex border border-zinc-800 px-2 py-1.5 bg-black/40"
            />
            {isAuthed ? (
              <>
                <GlobalSearchButton onOpen={() => setSearchOpen(true)} />
                <TokenBalance
                  variant="compact"
                  className="ml-1 border border-zinc-800 px-2 py-1.5 bg-black/40"
                />
                <ChitsTickerPill className="ml-1 hidden sm:inline-flex" />
                <InnerLightWalletPill className="ml-1 hidden sm:inline-flex" />
                <HeroLevelPill className="ml-1" />
                <GuildBuffsBadge className="ml-1 hidden md:inline-flex" compact />
                <DailyQuestsPill />
                <FestivalBoardPill />
                <RaidInboxPill className="ml-1" />
                <NotificationsBell />
                <InventoryBagButton onOpen={() => setBagOpen(true)} />
                {user.role === "owner" && (
                  <AdminMenu />
                )}
                <button
                  data-testid="nav-profile"
                  onClick={goProfile}
                className="ml-2 relative w-10 h-10 border-2 border-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] hover:text-black transition-colors"
                title={user.role === "owner" ? `Owner · ${user.name}` : `Profile · ${user.name}`}
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-contain bg-black" />
                ) : (
                  <span className="font-display text-sm">{user.name.charAt(0).toUpperCase()}</span>
                )}
                {user.role === "owner" && (
                  <span
                    data-testid="owner-crown"
                    className="absolute -top-2 -right-2 bg-[#D4AF37] text-black w-5 h-5 flex items-center justify-center rounded-full border border-black"
                    title="Owner"
                  >
                    <Crown size={11} strokeWidth={2.5} />
                  </span>
                )}
              </button>
              </>
            ) : (
              <button
                data-testid="nav-signin"
                onClick={() => setAuthOpen(true)}
                className="ml-2 btn-omni btn-outline-gold"
              >
                <LogIn size={14} /> Sign In
              </button>
            )}
          </nav>

          {/* Mobile-only: surface Bell + Bag next to the hamburger so authed
              users can reach them without opening the full menu. Messages
              lives in the bottom dock now. */}
          {isAuthed && (
            <div className="flex lg:hidden items-center gap-1.5">
              <GlobalSearchButton onOpen={() => setSearchOpen(true)} />
              <NotificationsBell />
              <InventoryBagButton onOpen={() => setBagOpen(true)} />
            </div>
          )}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white"
            onClick={() => setOpen((s) => !s)}
            aria-label="Toggle navigation"
          >
            {open ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-black border-t border-zinc-800">
          <div className="px-4 py-6 flex flex-col gap-4">
            {NAV.map((n) => {
              if (n.isDropdown && n.id === "train") {
                return (
                  <div key={n.id} data-testid="mobile-nav-train-section"
                       className="flex flex-col gap-2 border-y border-amber-700/40 py-3 my-1">
                    <div className="font-accent text-base uppercase tracking-widest text-[#D4AF37] inline-flex items-center gap-2">
                      <span>🏋️ {n.label}</span>
                      <span className="text-[10px] text-neutral-500">— wellness & coaching</span>
                    </div>
                    {TRAIN_ITEMS.map((t) => (
                      <button key={t.id}
                              data-testid={`mobile-nav-train-${t.id}`}
                              onClick={() => { setOpen(false); goTo(t.id); }}
                              className="flex items-center gap-3 pl-4 py-1.5 text-left hover:text-[#D4AF37] text-neutral-200">
                        <span className="text-xl">{t.icon}</span>
                        <span className="font-accent text-sm uppercase tracking-wider">{t.label}</span>
                      </button>
                    ))}
                  </div>
                );
              }
              if (n.isDropdown && n.id === "travel") {
                return (
                  <div key={n.id} data-testid="mobile-nav-travel-section"
                       className="flex flex-col gap-2 border-y border-amber-700/40 py-3 my-1">
                    <div className="font-accent text-base uppercase tracking-widest text-[#D4AF37] inline-flex items-center gap-2">
                      <span>🗺️ {n.label}</span>
                      <span className="text-[10px] text-neutral-500">— choose destination</span>
                    </div>
                    {TRAVEL_PLACES.map((p) => (
                      <button key={p.id}
                              data-testid={`mobile-nav-travel-${p.id}`}
                              onClick={() => { setOpen(false); window.location.href = p.path; }}
                              className="flex items-center gap-3 pl-4 py-1.5 text-left hover:text-[#D4AF37] text-neutral-200">
                        <span className="text-xl">{p.icon}</span>
                        <span className="font-accent text-sm uppercase tracking-wider">{p.label}</span>
                      </button>
                    ))}
                  </div>
                );
              }
              return (
                <button
                  key={n.id}
                  data-testid={`mobile-nav-${n.id}`}
                  onClick={() => { if (n.id === "hall") dismissHallTip(); goTo(n.id); }}
                  className={
                    "font-accent text-base uppercase tracking-widest text-left inline-flex items-center gap-2 hover:text-[#D4AF37] " +
                    (n.id === "hall" && hallPulse ? "hall-pulse" : "text-neutral-200")
                  }
                >
                  <span>{n.label}</span>
                  {n.id === "hall" && hallPulse && (
                    <span className="hall-new-badge text-[9px] font-bold tracking-[0.15em] bg-[#D4AF37] text-black px-1.5 py-[1px] leading-none">
                      NEW
                    </span>
                  )}
                </button>
              );
            })}
            <button
              data-testid="mobile-nav-cta"
              onClick={() => goTo("ai-coach")}
              className="btn-omni btn-red w-full"
            >
              Get My Plan
            </button>
            {isAuthed ? (
              <button
                data-testid="mobile-nav-profile"
                onClick={goProfile}
                className="btn-omni btn-outline-gold w-full"
              >
                <UserIcon size={14} /> My Profile
              </button>
            ) : (
              <button
                data-testid="mobile-nav-signin"
                onClick={() => { setOpen(false); setAuthOpen(true); }}
                className="btn-omni btn-outline-gold w-full"
              >
                <LogIn size={14} /> Sign In
              </button>
            )}
            {/* 🔧 Oct 2026 — owner Admin link in the mobile drawer. The
                desktop AdminMenu is `hidden md:inline-flex`, so phone
                owners had no way to reach the tools without typing /admin
                into the address bar. */}
            {user?.role === "owner" && (
              <a
                data-testid="mobile-nav-admin"
                href="/admin"
                onClick={() => setOpen(false)}
                className="btn-omni btn-outline-gold w-full flex items-center justify-center gap-2 border-[#D4AF37] text-[#D4AF37]"
              >
                🔧 Admin Tools
              </a>
            )}
          </div>
        </div>
      )}

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <InventoryBag open={bagOpen} onClose={() => setBagOpen(false)} />
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}

// 🔧 Owner-only admin menu — pill that opens a dropdown listing every
// admin tool. Repoints "Admin" from /admin/partners (single target) to a
// full grid with /admin as the fallback landing.
const ADMIN_TOOLS = [
  { path: "/admin/heroes",     icon: "🦸", label: "Hero Stats" },
  { path: "/admin/bosses",     icon: "👁️", label: "Boss Stats" },
  { path: "/admin/gear",       icon: "🛡️", label: "Gear Catalog" },
  { path: "/admin/promo-codes", icon: "🎟️", label: "Promo Codes" },
  { path: "/admin/partners",   icon: "🤝", label: "Partners" },
  { path: "/admin/supporters", icon: "💎", label: "Supporters" },
  { path: "/admin/faqs",       icon: "❓", label: "FAQs" },
  { path: "/admin/billing",    icon: "💳", label: "Billing" },
];

function AdminMenu() {
  const [open, setOpen] = useState(false);
  const ref = useState(null)[0];
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const el = document.querySelector('[data-testid="nav-admin-menu"]');
      if (el && !el.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);
  return (
    <div className="relative hidden md:inline-flex ml-1" data-testid="nav-admin-menu">
      <button
        onClick={() => setOpen((s) => !s)}
        data-testid="nav-admin-btn"
        title="Admin tools"
        className={`text-[9px] uppercase tracking-[0.25em] font-bold border px-2 py-1.5 transition-colors ${
          open
            ? "bg-[#D4AF37] text-black border-[#D4AF37]"
            : "border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
        }`}
      >
        🔧 Admin
      </button>
      {open && (
        <div
          data-testid="nav-admin-dropdown"
          className="absolute right-0 top-full mt-1 z-50 bg-[#0A0A0A] border-2 border-[#D4AF37] min-w-[220px] py-1 shadow-2xl"
        >
          <a
            href="/admin"
            className="block px-3 py-2 text-[11px] uppercase tracking-widest font-bold text-[#FCD34D] hover:bg-[#D4AF37] hover:text-black border-b border-neutral-900"
            data-testid="nav-admin-landing"
          >
            🔧 All Tools
          </a>
          {ADMIN_TOOLS.map((t) => (
            <a
              key={t.path}
              href={t.path}
              className="flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-widest text-neutral-300 hover:bg-[#D4AF37]/15 hover:text-[#FCD34D] transition-colors"
              data-testid={`nav-admin-${t.path.replace("/admin/", "")}`}
            >
              <span className="text-base leading-none">{t.icon}</span>
              <span>{t.label}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
